import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  GAP_PX,
  FALLBACK_TOOLTIP_H,
  TOOLTIP_PORTAL_Z_INDEX,
  computeHoverPanelHorizontal,
  computeVerticalPlacement,
  setTooltipPackIfChanged,
} from "./tooltipPortalLayout.js";

/**
 * Click-to-open anchored popover for DICOM series tiles (Images / upload imaging).
 * Split from Tooltip.js so FeedCard / comments etc. keep hover-based tooltips.
 */
function SeriesThumbnailPopover({ children, content, onOpen }) {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);
  const animationFrameRef = useRef(null);
  const tooltipPopoverRef = useRef(null);
  const stylePackRef = useRef("");
  const naturalHeightRef = useRef(0);
  const isClampedRef = useRef(false);
  const visibleRef = useRef(false);
  visibleRef.current = visible;

  const closePanel = useCallback(() => {
    stylePackRef.current = "";
    naturalHeightRef.current = 0;
    isClampedRef.current = false;
    setVisible(false);
    setTooltipStyle(null);
  }, []);

  const measureNaturalHeight = () => {
    const el = tooltipPopoverRef.current;
    if (!el) return naturalHeightRef.current || FALLBACK_TOOLTIP_H;
    const rendered = el.getBoundingClientRect().height;
    if (!isClampedRef.current && rendered > 0) {
      naturalHeightRef.current = rendered;
    }
    return (
      Math.max(naturalHeightRef.current, el.scrollHeight) ||
      rendered ||
      FALLBACK_TOOLTIP_H
    );
  };

  const seedPositionBelowTrigger = () => {
    const trigger = containerRef.current;
    if (!trigger) return;
    const trig = trigger.getBoundingClientRect();
    const { left, transform } = computeHoverPanelHorizontal(
      trig,
      window.innerWidth
    );
    setTooltipPackIfChanged(stylePackRef, setTooltipStyle, {
      top: trig.bottom + GAP_PX,
      left,
      transform,
    });
  };

  const openPanel = () => {
    seedPositionBelowTrigger();
    setVisible(true);
    if (typeof onOpen === "function") onOpen();
  };

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (visible) {
      closePanel();
    } else {
      openPanel();
    }
  };

  useEffect(() => {
    if (!visible) return undefined;

    const onDocMouseDown = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (containerRef.current?.contains(t)) return;
      if (tooltipPopoverRef.current?.contains(t)) return;
      closePanel();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closePanel();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, closePanel]);

  useEffect(() => {
    if (!visible) {
      stylePackRef.current = "";
      return undefined;
    }

    const refinePosition = () => {
      if (!visibleRef.current) return;

      const trigger = containerRef.current;
      if (!trigger) return;

      const trig = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const { left, transform } = computeHoverPanelHorizontal(
        trig,
        viewportWidth
      );

      const tooltipH = measureNaturalHeight();

      const { top, maxHeight } = computeVerticalPlacement(
        trig,
        tooltipH,
        viewportHeight
      );

      isClampedRef.current = typeof maxHeight === "number";

      setTooltipPackIfChanged(stylePackRef, setTooltipStyle, {
        top,
        left,
        transform,
        maxHeight,
      });

      animationFrameRef.current = requestAnimationFrame(refinePosition);
    };

    refinePosition();
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [visible]);

  const renderPopover = () => {
    if (!visible) return null;

    const appRoot = document.querySelector(".App") || document.body;

    return ReactDOM.createPortal(
      <div
        ref={tooltipPopoverRef}
        role="presentation"
        className="tooltip-2-text"
        style={
          tooltipStyle ?? {
            position: "fixed",
            left: 0,
            top: 0,
            visibility: "hidden",
            pointerEvents: "none",
            zIndex: TOOLTIP_PORTAL_Z_INDEX,
          }
        }
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {React.cloneElement(content, {
          onClose: () => closePanel(),
        })}
      </div>,
      appRoot
    );
  };

  return (
    <div
      ref={containerRef}
      className="tooltip-2-container"
      onClick={handleTriggerClick}
      style={{
        position: "relative",
        display: "inline-block",
        cursor: "pointer",
      }}
    >
      {children}
      {renderPopover()}
    </div>
  );
}

export default SeriesThumbnailPopover;
